import { useEffect } from 'react';
import Head from 'next/head';
import PropTypes from 'prop-types';
import dynamic from 'next/dynamic';
import NavigationDesktop from '../../components/NavigationDesktop/NavigationDesktop';
import Footer from '../../components/Footer/Footer';
import s from './BasePage.module.scss';
import NavigationMobile from '../../components/NavigationMobile/NavigationMobile';
import { trackThreePageViews } from '../../utils/dataLayer';
import SkipContent from '../../components/SkipContent/SkipContent';
import { useTranslation } from 'next-i18next';
const WagtailUserbar = dynamic(() => import('../../components/WagtailUserbar'));

const BasePage = ({
  children,
  seo = {},
  shouldRenderSeo = true,
  wagtailUserbar,
  siteSetting,
  pageType,
}) => {
  const {
    seoHtmlTitle,
    seoMetaDescription,
    seoOgTitle,
    seoOgDescription,
    seoOgUrl,
    seoOgImage,
    seoOgType,
    seoTwitterTitle,
    seoTwitterDescription,
    seoTwitterUrl,
    seoTwitterImage,
    seoMetaRobots,
    canonicalLink,
  } = seo;
  const { t } = useTranslation();

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const hasTracked = sessionStorage.getItem('threePageViewsTracked');
    if (hasTracked) return;

    let views = Number(sessionStorage.getItem('pageViews') || 0) + 1;
    sessionStorage.setItem('pageViews', views);

    if (views >= 3) {
      trackThreePageViews();
      sessionStorage.setItem('threePageViewsTracked', 'true');
    }
  }, []);

  const fallbackMetaDescription =
    seoMetaDescription ||
    `${seoHtmlTitle} – ${t('Generic.siteMetaDescriptionFallback')}`;

  return (
    <>
      {shouldRenderSeo && (
        <Head>
          <title>{seoHtmlTitle}</title>
          <link rel="icon" href="/favicon.ico" />
          <meta name="description" content={fallbackMetaDescription} />
          {!!seoOgTitle && <meta property="og:title" content={seoOgTitle} />}
          {!!seoOgDescription && (
            <meta property="og:description" content={seoOgDescription} />
          )}
          {!!seoOgUrl && <meta property="og:url" content={seoOgUrl} />}
          {!!seoOgImage && <meta property="og:image" content={seoOgImage} />}
          {!!seoOgType && <meta property="og:type" content={seoOgType} />}
          {!!seoTwitterTitle && (
            <meta property="twitter:title" content={seoTwitterTitle} />
          )}
          {!!seoTwitterDescription && (
            <meta
              property="twitter:description"
              content={seoTwitterDescription}
            />
          )}
          {!!seoTwitterUrl && (
            <meta property="twitter:url" content={seoTwitterUrl} />
          )}
          {!!seoTwitterImage && (
            <meta property="twitter:image" content={seoTwitterImage} />
          )}
          {!!seoMetaRobots && (
            <meta name="robots" content={seoMetaRobots.value} />
          )}
          {!!canonicalLink && <link rel="canonical" href={canonicalLink} />}
        </Head>
      )}
      <div className={s.BasePage}>
        <SkipContent />
        <div className={s.NavDesktop}>
          <NavigationDesktop
            navigation={siteSetting?.navigation}
            siteTitle={siteSetting?.siteTitle}
            languages={siteSetting?.languages}
            modifiers={[pageType]}
          />
        </div>
        <div className={s.NavMobile}>
          <NavigationMobile
            navigation={siteSetting?.navigation}
            languages={siteSetting?.languages}
          />
        </div>

        <main className={s.MainContent} id="main-content">
          {children}
        </main>

        <footer className={s.Footer}>
          <Footer
            {...siteSetting?.footer}
            modifiers={[pageType]}
            cookieConsent={siteSetting?.cookieConsent}
          />
        </footer>
      </div>
      {!!wagtailUserbar && <WagtailUserbar {...wagtailUserbar} />}
    </>
  );
};

BasePage.propTypes = {
  children: PropTypes.node,
  seo: PropTypes.shape({
    seoHtmlTitle: PropTypes.string,
    seoMetaDescription: PropTypes.string,
    seoOgTitle: PropTypes.string,
    seoOgDescription: PropTypes.string,
    seoOgUrl: PropTypes.string,
    seoTwitterTitle: PropTypes.string,
    seoMetaRobots: PropTypes.shape({
      index: PropTypes.bool,
      follow: PropTypes.bool,
      value: PropTypes.string,
    }),
  }),
  shouldRenderSeo: PropTypes.bool,
  wagtailUserbar: PropTypes.shape({
    html: PropTypes.string,
  }),
};

export default BasePage;
